import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X,
  Bell,
  Package,
  Check,
  AlertCircle
} from 'lucide-react';
import { AlertTypeSelector } from './AlertTypeSelector';
import { AlertEmailSelector } from './AlertEmailSelector';
import {
  AlertInput,
  AlertType,
  TriggerConfig,
  ProductForAlert
} from '../../types/alerts';
import { useAlerts } from '../../hooks/useAlerts';
import { useAlertEmails } from '../../hooks/useAlertEmails';

interface BulkAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProducts: ProductForAlert[];
  currentTableName?: string;
  // Opcional: crear por SKU
  skuCode?: string;
  // Opcional: crear por grupo
  comparisonGroupId?: string;
}

export const BulkAlertModal: React.FC<BulkAlertModalProps> = ({
  isOpen,
  onClose,
  selectedProducts,
  currentTableName,
  skuCode,
  comparisonGroupId
}) => {
  const { t } = useTranslation();
  const { createAlert } = useAlerts();
  const { trackMultipleEmails } = useAlertEmails();

  // Estados del formulario
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [alertType, setAlertType] = useState<AlertType | null>(null);
  const [triggerConfig, setTriggerConfig] = useState<TriggerConfig>({});
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);

  // Estados de UI
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (isOpen) {
      setName('');
      setDescription('');
      setIsPublic(true);
      setAlertType(null);
      setTriggerConfig({});
      setEmailEnabled(false);
      setEmailRecipients([]);
      setError(null);
      setSuccess(false);
    }
  }, [isOpen]);

  // Determinar modo
  const isBySku = !!skuCode;
  const isByGroup = !!comparisonGroupId;
  const isIndividual = !isBySku && !isByGroup;

  // Validar formulario
  const validateForm = (): string | null => {
    if (!alertType) {
      return t('alerts.validation.type_required');
    }

    if (isIndividual && selectedProducts.length === 0) {
      return t('alerts.validation.products_required');
    }

    if (emailEnabled && emailRecipients.length === 0) {
      return t('alerts.validation.emails_required');
    }

    return null;
  };

  // Guardar
  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const fallbackName = name.trim() || t('alerts.default_name', 'Alerta');

      if (isBySku) {
        // Crear alerta por SKU
        const alertData: AlertInput = {
          name: fallbackName,
          description: description.trim() || undefined,
          isPublic,
          alertType: alertType!,
          triggerConfig,
          assignmentType: 'sku',
          skuCode: skuCode,
          isActive: true,
          emailNotification: {
            enabled: emailEnabled,
            recipients: emailRecipients
          }
        };
        await createAlert(alertData);
      } else if (isByGroup) {
        // Crear alerta por grupo de comparación
        const alertData: AlertInput = {
          name: fallbackName,
          description: description.trim() || undefined,
          isPublic,
          alertType: alertType!,
          triggerConfig,
          assignmentType: 'comparison_group',
          comparisonGroupId: comparisonGroupId,
          isActive: true,
          emailNotification: {
            enabled: emailEnabled,
            recipients: emailRecipients
          }
        };
        await createAlert(alertData);
      } else {
        // Crear alerta individual para todos los productos seleccionados
        const alertData: AlertInput = {
          name: fallbackName,
          description: description.trim() || undefined,
          isPublic,
          alertType: alertType!,
          triggerConfig,
          assignmentType: 'individual',
          productIds: selectedProducts.map(p => p.id_catalogo),
          isActive: true,
          emailNotification: {
            enabled: emailEnabled,
            recipients: emailRecipients
          }
        };
        await createAlert(alertData);
      }

      // Registrar emails usados
      if (emailEnabled && emailRecipients.length > 0) {
        await trackMultipleEmails(emailRecipients);
      }

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Error creating bulk alert:', err);
      setError(err instanceof Error ? err.message : t('alerts.error_saving'));
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Bell className="h-6 w-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {t('alerts.bulk_create')}
              </h2>
              <p className="text-sm text-gray-500">
                {isBySku && `SKU: ${skuCode}`}
                {isByGroup && t('alerts.for_comparison_group')}
                {isIndividual && t('alerts.for_selected_products', { count: selectedProducts.length })}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Success message */}
          {success && (
            <div className="flex items-center p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              <Check className="h-5 w-5 mr-2" />
              <span>{t('alerts.bulk_created_success')}</span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg text-red-700">
              <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          {!success && (
            <>
              {/* Resumen de productos */}
              {isIndividual && selectedProducts.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <Package className="h-5 w-5 text-blue-600" />
                    <span className="font-medium text-blue-900">
                      {selectedProducts.length} {t('alerts.products_selected')}
                    </span>
                  </div>
                  {selectedProducts.length <= 5 && (
                    <div className="space-y-1">
                      {selectedProducts.map(product => (
                        <p key={product.id_catalogo} className="text-sm text-blue-800 truncate">
                          • {product.title}
                        </p>
                      ))}
                    </div>
                  )}
                  {selectedProducts.length > 5 && (
                    <p className="text-sm text-blue-700">
                      {t('alerts.and_more_products', { count: selectedProducts.length - 5 })}
                    </p>
                  )}
                </div>
              )}

              {/* Selector de tipo */}
              <div className="border-t border-gray-200 pt-4">
                <AlertTypeSelector
                  selectedType={alertType}
                  triggerConfig={triggerConfig}
                  onTypeChange={setAlertType}
                  onConfigChange={setTriggerConfig}
                />
              </div>

              {/* Notificaciones por email */}
              <div className="border-t border-gray-200 pt-4">
                <AlertEmailSelector
                  selectedEmails={emailRecipients}
                  onEmailsChange={setEmailRecipients}
                  enabled={emailEnabled}
                  onEnabledChange={setEmailEnabled}
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className="flex items-center justify-end space-x-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  {t('common.saving')}
                </>
              ) : (
                <>
                  <Bell className="h-4 w-4 mr-2" />
                  {t('alerts.create_alert')}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
